<template>
    <div>
        <h1>Flytta fastigheten '{{this.buildingName}}'</h1>
        <hr />
        <b-form @submit.prevent="onSubmit">

            <b-form-group id="input-group-customer" label="Kund" label-for="input-customer">
                <v-select ref="customer-select-list"
                          v-model="selectedCustomer"
                          placement="top"
                          :filterable="false"
                          :options="customers"
                          :clearable="false"
                          @search="onSearch"
                          @option:selected="onSelect"
                          @option:blur="onBlur"
                          @click.stop=""
                          v-on:keyup.stop="">
                    <template slot="no-options">
                        {{isSuperAdmin ? 'Sök' : 'Välj'}}
                    </template>
                    <template slot="option" slot-scope="option">
                        <div class="d-center">
                            {{ option.name }} ({{ option.customerNo }})
                        </div>
                    </template>
                    <template slot="selected-option" slot-scope="option">
                        <div class="selected d-center">
                            {{ option.name }} ({{ option.customerNo }})
                        </div>
                    </template>
                </v-select>
            </b-form-group>

            <b-form-group id="input-group-name" label="Schemaansvarig" label-for="input-responsible">
                <building-schedule-responsible-select-list id="input-responsible"
                                                           :building-id="buildingId"
                                                           :customer-id="form.customerId"
                                                           :is-super-admin="isSuperAdmin"
                                                           v-model="form.name"
                                                           :disabled="isSubmitting || !form.customerId">
                </building-schedule-responsible-select-list>

                <b-form-text id="input-name-help-block">
                    <ul class="text-danger mb-0">
                        <li v-for="message in validationByKey('Name')" :key="message">
                            {{ message }}
                        </li>
                    </ul>
                </b-form-text>
            </b-form-group>

            <b-alert :show="validationByKey('Other').length > 0" variant="danger">
                <ul class="text-danger mb-0">
                    <li v-for="message in validationByKey('Other')" :key="message">
                        {{ message }}
                    </li>
                </ul>
            </b-alert>

            <b-button v-b-tooltip.hover
                      variant="primary"
                      class="float-right"
                      :disabled="isSubmitting || !form.customerId || !form.name"
                      type="submit"
                      :title="isSubmitting ? 'Skapar...' : ''">
                <font-awesome-icon v-if="isSubmitting" icon="spinner" spin /> Flytta
            </b-button>
        </b-form>
    </div>
</template>

<script>
    import {
        mapState,
        mapActions,
        mapGetters,
    } from 'vuex';
    import {
        get,
        post,
    } from '@/helpers/api';
    import { debounce } from '@/helpers/input.helper';
    import BuildingScheduleResponsibleSelectList from '@/components/building/BuildingScheduleResponsibleSelectList';

    export default {
        name: 'MoveBuilding',
        components: {
            BuildingScheduleResponsibleSelectList,
        },
        props: {
            buildingId: {
                type: Number,
                required: true
            },
            buildingName: {
                type: String,
                required: true
            },
        },
        data() {
            return {
                steps: ['selectCustomer', 'selectCustomerResponsible'],
                isSubmitting: false,
                isChanging: false,
                allCustomers: [],
                customers: [],
                selected: null,
                selectedCustomer: null,
                form: {
                    customerId: null,
                    name: '',
                },
                validationErrors: null,
            };
        },
        watch: {
            userIsLoading: {
                handler(newValue) {
                    if (!newValue) {
                        if (!this.isSuperAdmin) {
                            get('Customer', 'GetAllAvailableForUser').then((x) => {
                                this.allCustomers = x.data;
                                this.customers = x.data;
                            });
                        }
                    }
                },
                immediate: true,
            },
        },
        computed: {
            ...mapGetters('user', ['isInRole']),
            isSuperAdmin() {
                return this.isInRole('SuperAdmin');
            },
        },
        methods: {
            onSearch: debounce(
                function (value) {
                    const trimmed = value.trim();
                    if (trimmed.length > 1) {
                        this.searchCustomers(trimmed);
                    }
                },
                250,
                false,
            ),
            async searchCustomers(query) {
                if (this.isSuperAdmin) {
                    // NY ENDPOINT FÖR SUPERADMIN – query mot alla kunder i org
                    await get(
                        'Customer',
                        `GetAllByQuery?query=${encodeURIComponent(query)}`
                    ).then((x) => {
                        this.customers = x.data;
                    });
                } else {
                    // Övriga använder fortfarande sina tillgängliga kunder per user
                    await get(
                        'Customer',
                        `GetAvailableCustomersForUserByQuery?query=${encodeURIComponent(query)}`
                    ).then((x) => {
                        this.customers = x.data;
                    });
                }
            },
            onSelect(item) {
                this.selectedCustomer = item;
                this.form.customerId = parseInt(item.id);

                this.form.name = null;

                // Set select lists customers to all customers.
                if (!this.isSuperAdmin) {
                    this.customers = this.allCustomers;
                }

                this.isChanging = false;
            },
            onBlur() {
                this.isChanging = false;
            },
            async onSubmit() {
                // Resets.
                this.validationErrors = null;
                this.isSubmitting = true;

                const payload = {
                    customerId: this.form.customerId,
                    buildingId: this.buildingId,
                    scheduleResponsibleUserId: this.form.name   // v-model från ansvarig-selecten
                };

                await post('Building', 'Move', payload)
                    .then((x) => {
                        this.validationErrors = x.data.errors;
                    })
                    .catch((x) => {
                        // Map errors.
                        this.validationErrors = x.response && x.response.data
                            ? x.response.data.errors
                            : null;
                    });

                this.isSubmitting = false;

                // On created/moved.
                if (!this.validationErrors) {
                    this.onMoved();
                }
            },
            onMoved() {
                this.$bvToast.toast('Fastigheten har flyttats', {
                    title: 'Flyttad',
                    variant: 'success',
                    solid: true,
                    toaster: 'b-toaster-top-center',
                    autoHideDelay: 1000
                });

                setTimeout(() => {
                    this.$router.push({ name: 'manage-building-list' });
                }, 1000);
            },
            validationByKey(code) {
                return this.validationErrors ? this.validationErrors[code] || [] : [];
            },
            resetForm() {
                this.form = {
                    name: ''
                };
                this.validationErrors = null;
            }
        }
    };
</script>

<style scoped>
    .form-group {
        margin-bottom: 0.5rem;
    }
</style>
